/**
 * Payout notices: the one-line settlements the phone and TV read out once the
 * chips move. Pure builders over the broadcast types — no socket, no DOM — so
 * the wording stays identical everywhere and view-model tests can assert
 * strings instead of pixels.
 */
import {
  BACCARAT_SIDE_BETS,
  SIDE_BETS,
  type PublicBaccaratSideBet,
  type PublicSideBet,
} from '@tv-casino/shared';
import { formatSignedChips } from './format.js';

/** How a settlement should read on screen: green, neutral, red, or not yet. */
export type PayoutTone = 'win' | 'push' | 'loss' | 'pending';

/** The ":1" odds a settled stake actually paid, or null when nothing was won. */
function oddsLabel(stake: number, payout: number): string | null {
  if (payout <= stake) return null;
  return `${(payout - stake) / stake}:1`;
}

/**
 * The table's blackjack ratio in felt language: 1.5 reads "3:2", 1.2 reads
 * "6:5". Anything unusual falls back to its raw value, which is still true.
 */
export function blackjackPayoutLabel(blackjackPayout: number): string {
  const known: Record<string, string> = {
    '1.5': '3:2',
    '1.2': '6:5',
    '2': '2:1',
    '1': '1:1',
  };
  return known[String(blackjackPayout)] ?? `${blackjackPayout}:1`;
}

/** The felt-plaque shape every side stake wears once the TV picks it up. */
export interface SideStakeToken {
  name: string;
  stake: number;
  mark: string | null;
  tone: PayoutTone;
}

function stakeToken(name: string, bet: { stake: number; payout: number | null }): SideStakeToken {
  if (bet.payout == null) {
    return { name, stake: bet.stake, mark: null, tone: 'pending' };
  }
  if (bet.payout === 0) {
    return { name, stake: bet.stake, mark: formatSignedChips(-bet.stake), tone: 'loss' };
  }
  if (bet.payout === bet.stake) {
    return { name, stake: bet.stake, mark: 'push', tone: 'push' };
  }
  return { name, stake: bet.stake, mark: formatSignedChips(bet.payout - bet.stake), tone: 'win' };
}

/**
 * One blackjack side bet as a felt plaque: the bet's name, the stake chips
 * riding on it and — once the opening deal settles it — a compact mark, the
 * signed chips it moved or `push`. The phone keeps its prose notice; the TV
 * seats read this at a glance instead.
 */
export function sideBetToken(bet: PublicSideBet): SideStakeToken {
  return stakeToken(SIDE_BETS[bet.id]?.name ?? 'Side bet', bet);
}

/** Baccarat's pair bets tell the same story, one row shorter. */
export function baccaratSideBetToken(bet: PublicBaccaratSideBet): SideStakeToken {
  return stakeToken(BACCARAT_SIDE_BETS[bet.id]?.name ?? 'Pair bet', bet);
}

/** Three-card's Pair Plus rides the same plaque, so the TV can itemize it. */
export function pairPlusToken(bet: { stake: number; payout: number | null }): SideStakeToken {
  return stakeToken('Pair Plus', bet);
}

/** Baccarat's pair bets tell the same story, one row shorter. */
export function baccaratSideBetNotice(bet: PublicBaccaratSideBet): {
  text: string;
  tone: PayoutTone;
} {
  const name = BACCARAT_SIDE_BETS[bet.id]?.name ?? 'Side bet';
  if (bet.payout == null) {
    return { text: `${name} · ${bet.stake} staked`, tone: 'pending' };
  }
  if (bet.payout === 0) {
    return { text: `${name} · no pair · ${formatSignedChips(-bet.stake)}`, tone: 'loss' };
  }
  const odds = oddsLabel(bet.stake, bet.payout);
  return {
    text: `${name}${odds ? ` pays ${odds}` : ''} · ${formatSignedChips(bet.payout - bet.stake)}`,
    tone: 'win',
  };
}
